import { TaxIncentive } from "./types";
import { BudgetCategory } from "./types";
import {
  DEFAULT_TAX_INCENTIVE_ASSUMPTIONS,
  TaxIncentiveAssumptions,
  TaxNettingSummary,
} from "./taxIncentiveEffectiveRate.types";
import {
  computeBestJurisdictionEffectiveRate,
  toTaxNettingSummary,
} from "./taxIncentiveEffectiveRate.utils";
import { getStructuredTaxIncentiveProgram } from "./TaxIncentives/taxIncentiveStructuredCatalog";

export type { TaxNettingSummary } from "./taxIncentiveEffectiveRate.types";

const PERCENT_PATTERN = /(\d{1,2}(?:\.\d+)?)\s*%/g;

function extractPercentsFromText(text: string): number[] {
  const matches = [...text.matchAll(PERCENT_PATTERN)];
  return matches.map((match) => Number(match[1])).filter((value) => value > 0 && value <= 100);
}

export function parseIncentiveRebatePercent(
  locationName: string,
  incentivesText?: string
): number | null {
  const structured = getStructuredTaxIncentiveProgram(locationName);
  if (structured?.isActive) {
    return structured.maxHeadlineRate;
  }

  if (incentivesText) {
    const fromIncentive = extractPercentsFromText(incentivesText);
    if (fromIncentive.length > 0) {
      return Math.max(...fromIncentive);
    }
  }

  return null;
}

/** True when catalog amounts (minimums, caps, fees are in USD) can be compared to the budget. */
export function isTaxSavingsCurrencySupported(budgetCurrency: string | null | undefined): boolean {
  return (budgetCurrency || "USD").toUpperCase() === "USD";
}

/**
 * Incentives to net against the budget. No budget run links incentives today, so a budget
 * falls back to its production base state. The fallback needs a USD budget.
 */
export function resolveNettingTaxIncentives(
  linkedIncentives: TaxIncentive[] | null | undefined,
  productionBaseState: string | null | undefined,
  budgetCurrency: string | null | undefined
): TaxIncentive[] {
  if (linkedIncentives?.length) {
    return linkedIncentives;
  }
  if (!productionBaseState || !isTaxSavingsCurrencySupported(budgetCurrency)) {
    return [];
  }
  return [{ location_name: productionBaseState, incentives: "" }];
}

export function computeTaxIncentiveNetting(
  grossBudget: number,
  belowTheLineEstimate: number,
  taxIncentives?: TaxIncentive[] | null,
  categories?: BudgetCategory[] | null,
  assumptions: TaxIncentiveAssumptions = DEFAULT_TAX_INCENTIVE_ASSUMPTIONS
): TaxNettingSummary | null {
  if (!taxIncentives?.length || grossBudget <= 0) {
    return null;
  }

  const jurisdictionNames = [...new Set(taxIncentives.map((incentive) => incentive.location_name))];

  if (categories?.length) {
    const best = computeBestJurisdictionEffectiveRate(
      grossBudget,
      categories,
      jurisdictionNames,
      assumptions
    );
    if (best) {
      return toTaxNettingSummary({
        ...best,
        locationNames: jurisdictionNames,
      });
    }
  }

  const rebatePercents = taxIncentives
    .map((incentive) => parseIncentiveRebatePercent(incentive.location_name, incentive.incentives))
    .filter((value): value is number => value != null);

  if (rebatePercents.length === 0) {
    return null;
  }

  const rebatePercent = Math.max(...rebatePercents);
  const eligibleSpend = belowTheLineEstimate;
  const estimatedRebate = (eligibleSpend * rebatePercent) / 100;
  const netCost = Math.max(grossBudget - estimatedRebate, 0);
  const primaryJurisdiction = jurisdictionNames[0] ?? "Unknown";

  return {
    grossBudget,
    qualifiedSpend: eligibleSpend,
    eligibleSpend,
    headlineCredit: estimatedRebate,
    totalFriction: 0,
    effectiveNetCredit: estimatedRebate,
    estimatedRebate,
    netCost,
    headlineRatePercent: rebatePercent,
    effectiveRatePercent: rebatePercent,
    rebatePercent,
    locationNames: jurisdictionNames,
    primaryJurisdiction,
    frictionBreakdown: [],
    meetsMinimumSpend: true,
  };
}
